import type { TConversationMessage } from '@cascade/api-client/conversations';
import { createReducer } from '@cascade/core/utils';
import {
  addToolCall,
  isMessageUnfinished,
  isUserMessage,
  resolveToolCalls,
} from './Conversations.helpers.ts';
import type {
  TConversationAction,
  TConversationState,
  TConversationTransition,
  TMessage,
} from './Conversations.types.ts';

export const createInitialState = (
  conversationId?: string,
): TConversationState => ({
  conversationId,
  hasLoadedMessages: conversationId === undefined,
  hasStaleMessages: false,
  messages: [],
});

const getQuestionId = (requestId: string) => `${requestId}/question`;

const getAnswerId = (requestId: string) => `${requestId}/answer`;

const findMessage = (
  state: TConversationState,
  requestId: string | undefined,
  sender: string,
): number =>
  requestId === undefined
    ? -1
    : state.messages.findIndex(
        (message) =>
          message.requestId === requestId && message.sender === sender,
      );

const updateMessage = (
  state: TConversationState,
  index: number,
  update: (message: TMessage) => TMessage,
): TConversationState =>
  index === -1
    ? state
    : {
        ...state,
        messages: state.messages.map((message, position) =>
          position === index ? update(message) : message,
        ),
      };

const updateAnswer = (
  state: TConversationState,
  requestId: string | undefined,
  update: (message: TMessage) => TMessage,
): TConversationState =>
  updateMessage(state, findMessage(state, requestId, 'bot'), (message) => {
    const updated = update(message);

    return { ...updated, toolCalls: resolveToolCalls(updated) };
  });

const selectConversation: TConversationTransition<'conversation.selected'> = (
  state,
  { conversationId },
) =>
  state.conversationId === conversationId && conversationId !== undefined
    ? state
    : createInitialState(conversationId);

const loadMessages: TConversationTransition<'messages.loaded'> = (
  state,
  { conversationId, messages },
) => {
  if (state.conversationId !== conversationId) {
    return state;
  }

  const loadedIds = new Set(messages.map((message) => message.id));
  const heldMessages = new Map(
    state.messages.map((message) => [message.id, message]),
  );

  const mergeLoadedMessage = (message: TConversationMessage): TMessage => {
    const heldMessage = heldMessages.get(message.id);

    if (heldMessage && isMessageUnfinished(heldMessage)) {
      return heldMessage;
    }

    return {
      ...heldMessage,
      ...message,
      key: heldMessage?.key ?? message.id,
      status: 'complete',
    };
  };

  return {
    ...state,
    hasLoadedMessages: true,
    hasStaleMessages: false,
    messages: [
      ...messages.map(mergeLoadedMessage),
      ...state.messages.filter((message) => !loadedIds.has(message.id)),
    ],
  };
};

const dropFailedReload: TConversationTransition<'messages.reloadFailed'> = (
  state,
) => ({
  ...state,
  hasStaleMessages: false,
});

const resyncAfterReconnect: TConversationTransition<'socket.reconnected'> = (
  state,
) => ({
  ...state,
  hasStaleMessages: state.conversationId !== undefined,
  messages: state.messages.filter((message) => !isMessageUnfinished(message)),
});

const startRequest: TConversationTransition<'chat.ask'> = (
  state,
  { envelope, files },
) => {
  const questionId = getQuestionId(envelope.request_id);
  const answerId = getAnswerId(envelope.request_id);

  return {
    ...state,
    messages: [
      ...state.messages,
      {
        id: questionId,
        key: questionId,
        sender: 'user',
        message: envelope.payload.question,
        files,
        status: 'pending',
        requestId: envelope.request_id,
      },
      {
        id: answerId,
        key: answerId,
        sender: 'bot',
        message: '',
        status: 'pending',
        requestId: envelope.request_id,
      },
    ],
  };
};

const removeRequest: TConversationTransition<'chat.ask.removed'> = (
  state,
  { requestId },
) => ({
  ...state,
  messages: state.messages.filter((message) => message.requestId !== requestId),
});

const recordFeedback: TConversationTransition<'message.feedback.sent'> = (
  state,
  { payload },
) =>
  updateMessage(
    state,
    state.messages.findIndex((message) => message.id === payload.message_id),
    (message) => ({
      ...message,
      feedback: payload.type,
      feedback_message: payload.feedback_message,
    }),
  );

const adoptConversation: TConversationTransition<'conversation.created'> = (
  state,
  { envelope },
) => {
  const isOwnRequest = findMessage(state, envelope.request_id, 'user') !== -1;

  return state.conversationId === undefined && isOwnRequest
    ? {
        ...state,
        conversationId: envelope.payload.conversation_id,
        hasLoadedMessages: true,
      }
    : state;
};

const recordMessage: TConversationTransition<'conversation.message.created'> = (
  state,
  { envelope },
) => {
  const { request_id: requestId, payload } = envelope;

  if (payload.conversation_id !== state.conversationId) {
    return state;
  }

  const named = state.messages.findIndex(
    (message) => message.id === payload.message_id,
  );

  const pending = findMessage(state, requestId, payload.sender);

  if (named !== -1 && pending !== -1 && named !== pending) {
    const { key } = state.messages[pending];
    const keyed = updateMessage(state, named, (message) => ({
      ...message,
      key,
    }));

    return {
      ...keyed,
      messages: keyed.messages.filter(
        (_message, position) => position !== pending,
      ),
    };
  }

  const index = named === -1 ? pending : named;

  if (index === -1) {
    return {
      ...state,
      messages: [
        ...state.messages,
        {
          id: payload.message_id,
          key: payload.message_id,
          sender: payload.sender,
          message: payload.message,
          date: payload.date,
          status: 'complete',
        },
      ],
    };
  }

  return updateMessage(state, index, (message) => ({
    ...message,
    id: payload.message_id,
    message: payload.message ?? message.message,
    date: payload.date ?? message.date,
    status: isUserMessage(payload) ? 'complete' : message.status,
  }));
};

const appendAnswerText: TConversationTransition<'chat.answer.token'> = (
  state,
  { envelope },
) =>
  updateAnswer(state, envelope.request_id, (message) =>
    message.status === 'complete' || message.status === 'failed'
      ? message
      : {
          ...message,
          message: `${message.message ?? ''}${envelope.payload.text}`,
          status: 'streaming',
        },
  );

const completeAnswer: TConversationTransition<'chat.answer.done'> = (
  state,
  { envelope },
) => {
  const index = findMessage(state, envelope.request_id, 'bot');

  if (index === -1) {
    return { ...state, hasStaleMessages: state.conversationId !== undefined };
  }

  return updateAnswer(state, envelope.request_id, (message) => ({
    ...message,
    id: envelope.payload.message_id ?? message.id,
    message: envelope.payload.answer ?? message.message,
    status: 'complete',
  }));
};

const failAnswer: TConversationTransition<'chat.answer.error'> = (
  state,
  { envelope },
) =>
  updateAnswer(state, envelope.request_id, (message) => ({
    ...message,
    status: 'failed',
    error: message.error ?? envelope.payload,
  }));

const recordToolCall: TConversationTransition<'chat.tool.called'> = (
  state,
  { envelope },
) =>
  updateAnswer(state, envelope.request_id, (message) => ({
    ...message,
    toolCalls: addToolCall(message.toolCalls, envelope.payload),
  }));

const attachDocuments: TConversationTransition<'chat.documents'> = (
  state,
  { envelope },
) =>
  updateAnswer(state, envelope.request_id, (message) => ({
    ...message,
    documents: envelope.payload.documents,
  }));

const attachDetectedExperience: TConversationTransition<
  'chat.experience.detected'
> = (state, { envelope }) =>
  updateAnswer(state, envelope.request_id, (message) => ({
    ...message,
    detectedExperience: envelope.payload,
  }));

const attachFollowUpQuestions: TConversationTransition<
  'chat.follow_up_questions'
> = (state, { envelope }) =>
  updateAnswer(state, envelope.request_id, (message) => ({
    ...message,
    followUpQuestions: envelope.payload.questions,
  }));

const markFileReady: TConversationTransition<'chat.file.ready'> = (
  state,
  { envelope },
) => {
  const { payload } = envelope;

  const index = state.messages.findIndex((message) =>
    message.files?.some((file) => file.file_id === payload.file_id),
  );

  return updateMessage(state, index, (message) => ({
    ...message,
    files: message.files?.map((file) =>
      file.file_id === payload.file_id ? payload : file,
    ),
  }));
};

const attachDeliveredFile: TConversationTransition<'chat.file.delivered'> = (
  state,
  { envelope },
) =>
  updateAnswer(state, envelope.request_id, (message) => ({
    ...message,
    files: [...(message.files ?? []), envelope.payload],
  }));

export const reduceConversation = createReducer<
  TConversationState,
  TConversationAction
>({
  'conversation.selected': selectConversation,
  'socket.reconnected': resyncAfterReconnect,
  'messages.reloadFailed': dropFailedReload,
  'messages.loaded': loadMessages,
  'chat.ask': startRequest,
  'chat.ask.removed': removeRequest,
  'message.feedback.sent': recordFeedback,
  'conversation.created': adoptConversation,
  'conversation.message.created': recordMessage,
  'chat.answer.token': appendAnswerText,
  'chat.answer.done': completeAnswer,
  'chat.answer.error': failAnswer,
  'chat.tool.called': recordToolCall,
  'chat.documents': attachDocuments,
  'chat.experience.detected': attachDetectedExperience,
  'chat.follow_up_questions': attachFollowUpQuestions,
  'chat.file.ready': markFileReady,
  'chat.file.delivered': attachDeliveredFile,
});
